import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useContent, useSectionMeta } from "../content/ContentContext";
import { Reveal, Chapter, EASE_OUT } from "./Reveal";
import { CharacterModal } from "./CharacterModal";

const CharacterCard = ({ c, index, onOpen }) => (
  <motion.button
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: index * 0.12, ease: EASE_OUT }}
    onClick={() => onOpen(c)}
    className="group relative text-left overflow-hidden rounded-sm aspect-[3/4] bg-coal focus:outline-none"
    data-testid={`character-card-${c.id}`}
  >
    <img
      src={c.img}
      alt={`${c.name} — ${c.role}`}
      className="absolute inset-0 w-full h-full object-cover img-cinematic transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 shadow-[inset_0_0_80px_rgba(184,134,11,0.22)]" />
    <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
      <p className="text-[10px] tracking-[0.34em] uppercase text-gold-light/90 mb-2">{c.role}</p>
      <h3 className="font-display text-3xl md:text-4xl text-ivory">{c.name}</h3>
      <p className="text-xs md:text-sm text-smoke mt-2 leading-relaxed max-w-[26ch]">{c.tagline}</p>
      <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold-light mt-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-500">
        Meet {c.name.charAt(0) + c.name.slice(1).toLowerCase()}
        <ArrowRight size={12} />
      </span>
    </div>
  </motion.button>
);

export const Family = () => {
  const [selected, setSelected] = useState(null);
  const { CHARACTERS, SITE } = useContent();
  const meta = useSectionMeta("family");

  return (
    <section id="family" className="relative py-24 md:py-36" data-testid="family-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Chapter number={meta.number} label={meta.label} id="family" />
        </Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
          <Reveal delay={0.08}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="family-heading">
              MEET THE <span className="italic text-gold-light">FAMILY</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-sm text-smoke max-w-xs leading-relaxed">
              {SITE.family.side}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {CHARACTERS.map((c, i) => (
            <CharacterCard key={c.id} c={c} index={i} onOpen={setSelected} />
          ))}
        </div>
      </div>

      <CharacterModal character={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </section>
  );
};
